import { generatePKCE } from './pkce';

const ANTHROPIC_CLIENT_ID = '9d1c250a-e61b-44d9-88ed-5944d1962f5e';
const ANTHROPIC_REDIRECT_URI = 'https://console.anthropic.com/oauth/code/callback';

// Scopes for Claude Pro/Max OAuth
const ANTHROPIC_SCOPES = 'org:create_api_key user:profile user:inference';

const STORAGE_PREFIX = 'anthropic_oauth_';

/**
 * Initiate Anthropic OAuth flow for Claude Pro/Max
 * Opens authorization URL in new tab for user to approve
 * User will copy the auth code back to our app
 *
 * Based on opencode-anthropic-auth implementation:
 * - Uses claude.ai/oauth/authorize
 * - State param = code verifier (OpenCode's approach)
 */
export async function initiateAnthropicOAuth(): Promise<string> {
  const { codeVerifier, codeChallenge } = await generatePKCE();

  // Store PKCE verifier for later retrieval
  sessionStorage.setItem(`${STORAGE_PREFIX}code_verifier`, codeVerifier);

  const url = new URL('https://claude.ai/oauth/authorize');
  url.searchParams.set('code', 'true');
  url.searchParams.set('client_id', ANTHROPIC_CLIENT_ID);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('redirect_uri', ANTHROPIC_REDIRECT_URI);
  url.searchParams.set('scope', ANTHROPIC_SCOPES);
  url.searchParams.set('code_challenge', codeChallenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('state', codeVerifier); // OpenCode uses verifier as state

  return url.toString();
}

/**
 * Retrieve stored PKCE values from session storage
 */
export function getStoredPkceValues(): { codeVerifier: string } | null {
  const codeVerifier = sessionStorage.getItem(`${STORAGE_PREFIX}code_verifier`);
  if (!codeVerifier) return null;
  return { codeVerifier };
}

/**
 * Clear stored PKCE values (call after OAuth exchange completes or fails)
 */
export function clearStoredPkceValues(): void {
  sessionStorage.removeItem(`${STORAGE_PREFIX}code_verifier`);
}

/**
 * Parse authorization code from user input
 * OpenCode splits by # - first part is code, second part is state
 */
export function parseAuthCode(rawCode: string): { code: string; state: string | undefined } {
  const splits = rawCode.split('#');
  return {
    code: splits[0],
    state: splits[1],
  };
}
